import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowUpRight, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  "Wedding",
  "Corporate Gala",
  "Private Party",
  "Birthday",
  "Other",
];

const SERVICE_OPTIONS = [
  "DJ Services",
  "Event Decor",
  "Sound Setup",
  "Lighting Setup",
  "Special Effects",
];

const inputBase =
  "w-full bg-black/40 border border-white/10 rounded-sm px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#FFFFFF]/60 focus:bg-black/60 transition-colors duration-200";

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.22em] text-white/50 mb-2 block">
        {label} {required && <span className="text-[#FFFFFF]">*</span>}
      </span>
      {children}
    </label>
  );
}

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_date: "",
    event_start_time: "",
    event_end_time: "",
    event_type: "",
    venue: "",
    guest_count: "",
    services: [],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const toggleService = (service) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(service)
        ? f.services.filter((s) => s !== service)
        : [...f.services, service],
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Inquiry sent! We'll be in touch within 24 hours.");
      setForm({
        name: "",
        email: "",
        phone: "",
        event_date: "",
        event_start_time: "",
        event_end_time: "",
        event_type: "",
        venue: "",
        guest_count: "",
        services: [],
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Unable to send right now. Please email ent.karnival@gmail.com directly.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#070707]"
    >
      <Toaster
        position="top-center"
        theme="dark"
        toastOptions={{
          style: {
            background: "#121212",
            color: "#FDFBF7",
            border: "1px solid rgba(212,175,55,0.3)",
          },
        }}
      />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          {/* Left — intro + details */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-[#FFFFFF]" />
              <span className="text-xs uppercase tracking-[0.28em] text-[#FFFFFF]">
                Get In Touch
              </span>
            </div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              data-testid="contact-headline"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] text-white tracking-tight"
            >
              Let&rsquo;s plan something{" "}
              <span className="italic text-[#FFFFFF]">amazing</span>.
            </motion.h2>

            <p className="mt-6 text-white/65 leading-relaxed text-base md:text-lg max-w-md">
              Ready to bring your event vision to life? Send us a note and
              we&rsquo;ll build a custom package that fits your event, style,
              and budget.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href="tel:+16137990786"
                data-testid="contact-phone"
                className="group flex items-center gap-4 p-4 border border-white/10 rounded-sm hover:border-[#FFFFFF]/50 hover:bg-white/[0.02] transition-all duration-300"
              >
                <div className="h-11 w-11 rounded-full bg-[#FFFFFF]/10 border border-[#FFFFFF]/30 flex items-center justify-center">
                  <Phone className="h-4 w-4 text-[#FFFFFF]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="text-[11px] uppercase tracking-widest text-white/40">
                    Phone
                  </div>
                  <div className="text-white text-base">613-799-0786</div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-white/30 group-hover:text-[#FFFFFF] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
              </a>

              <a
                href="mailto:ent.karnival@gmail.com"
                data-testid="contact-email"
                className="group flex items-center gap-4 p-4 border border-white/10 rounded-sm hover:border-[#FFFFFF]/50 hover:bg-white/[0.02] transition-all duration-300"
              >
                <div className="h-11 w-11 rounded-full bg-[#FFFFFF]/10 border border-[#FFFFFF]/30 flex items-center justify-center">
                  <Mail className="h-4 w-4 text-[#FFFFFF]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="text-[11px] uppercase tracking-widest text-white/40">
                    Email
                  </div>
                  <div className="text-white text-base">
                    ent.karnival@gmail.com
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-white/30 group-hover:text-[#FFFFFF] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
              </a>

              <div
                className="flex items-center gap-4 p-4 border border-white/10 rounded-sm"
                data-testid="contact-location"
              >
                <div className="h-11 w-11 rounded-full bg-[#FFFFFF]/10 border border-[#FFFFFF]/30 flex items-center justify-center">
                  <MapPin
                    className="h-4 w-4 text-[#FFFFFF]"
                    strokeWidth={1.5}
                  />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-white/40">
                    Location
                  </div>
                  <div className="text-white text-base">
                    Ottawa, Ontario, Canada
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right — real form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="md:col-span-7"
          >
            <div
              className="relative bg-[#0e0e0e] border border-white/10 rounded-sm overflow-hidden"
              data-testid="contact-form-card"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-black/40">
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#FFFFFF]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
                    <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
                  </div>
                  <span className="text-xs text-white/50 uppercase tracking-widest">
                    Event Inquiry Form
                  </span>
                </div>
                <span className="text-[10px] text-white/40 tracking-widest hidden sm:inline">
                  REPLY · WITHIN 24H
                </span>
              </div>

              {sent ? (
                <div
                  className="p-10 md:p-14 flex flex-col items-center text-center"
                  data-testid="contact-form-success"
                >
                  <div className="h-16 w-16 rounded-full bg-[#FFFFFF]/10 border border-[#FFFFFF]/40 flex items-center justify-center mb-6">
                    <CheckCircle2
                      className="h-7 w-7 text-[#FFFFFF]"
                      strokeWidth={1.5}
                    />
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl text-white tracking-tight">
                    Your inquiry is on its way.
                  </h3>
                  <p className="mt-4 text-white/60 max-w-md">
                    We&rsquo;ve received your message and will be in touch
                    within 24 hours. If it&rsquo;s urgent, call us at{" "}
                    <a
                      href="tel:+16137990786"
                      className="text-[#FFFFFF] hover:underline"
                    >
                      613-799-0786
                    </a>
                    .
                  </p>
                  <button
                    type="button"
                    onClick={() => setSent(false)}
                    data-testid="contact-form-send-another"
                    className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white/80 text-sm hover:border-[#FFFFFF] hover:text-[#FFFFFF] transition-colors"
                  >
                    Send another inquiry
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid="contact-form"
                  className="p-6 md:p-8 space-y-5"
                  noValidate
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Full Name" required>
                      <input
                        name="name"
                        type="text"
                        value={form.name}
                        onChange={onChange}
                        required
                        placeholder="Your name"
                        data-testid="contact-input-name"
                        className={inputBase}
                      />
                    </Field>
                    <Field label="Email Address" required>
                      <input
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={onChange}
                        required
                        placeholder="you@example.com"
                        data-testid="contact-input-email"
                        className={inputBase}
                      />
                    </Field>
                    <Field label="Phone">
                      <input
                        name="phone"
                        type="tel"
                        value={form.phone}
                        onChange={onChange}
                        placeholder="(613) 000-0000"
                        data-testid="contact-input-phone"
                        className={inputBase}
                      />
                    </Field>
                    <Field label="Event Date">
                      <input
                        name="event_date"
                        type="date"
                        value={form.event_date}
                        onChange={onChange}
                        data-testid="contact-input-date"
                        className={`${inputBase} appearance-none [color-scheme:dark]`}
                      />
                    </Field>
                    <Field label="Start Time">
                      <input
                        name="event_start_time"
                        type="time"
                        value={form.event_start_time}
                        onChange={onChange}
                        data-testid="contact-input-start-time"
                        className={`${inputBase} appearance-none [color-scheme:dark]`}
                      />
                    </Field>
                    <Field label="End Time">
                      <input
                        name="event_end_time"
                        type="time"
                        value={form.event_end_time}
                        onChange={onChange}
                        data-testid="contact-input-end-time"
                        className={`${inputBase} appearance-none [color-scheme:dark]`}
                      />
                    </Field>
                    <Field label="Event Type">
                      <select
                        name="event_type"
                        value={form.event_type}
                        onChange={onChange}
                        data-testid="contact-input-event-type"
                        className={`${inputBase} appearance-none pr-10 bg-[length:10px] bg-no-repeat bg-[right_1rem_center] cursor-pointer`}
                        style={{
                          backgroundImage:
                            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'><path d='M1 1L6 6L11 1' stroke='%23D4AF37' stroke-width='1.5' stroke-linecap='round'/></svg>\")",
                        }}
                      >
                        <option value="" className="bg-[#0e0e0e]">
                          Select event type
                        </option>
                        {EVENT_TYPES.map((t) => (
                          <option
                            key={t}
                            value={t}
                            className="bg-[#0e0e0e] text-white"
                          >
                            {t}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Estimated Guests">
                      <input
                        name="guest_count"
                        type="text"
                        inputMode="numeric"
                        value={form.guest_count}
                        onChange={onChange}
                        placeholder="e.g. 120"
                        data-testid="contact-input-guests"
                        className={inputBase}
                      />
                    </Field>
                  </div>

                  <Field label="Venue / Location">
                    <input
                      name="venue"
                      type="text"
                      value={form.venue}
                      onChange={onChange}
                      placeholder="Venue name or city (e.g. Hilton Lac-Leamy, Gatineau)"
                      data-testid="contact-input-venue"
                      className={inputBase}
                    />
                  </Field>

                  <Field label="Services You're Interested In">
                    <div
                      className="grid grid-cols-2 sm:grid-cols-3 gap-2"
                      data-testid="contact-input-services"
                    >
                      {SERVICE_OPTIONS.map((service) => {
                        const active = form.services.includes(service);
                        return (
                          <button
                            key={service}
                            type="button"
                            onClick={() => toggleService(service)}
                            data-testid={`contact-service-${service
                              .toLowerCase()
                              .replace(/\s+/g, "-")}`}
                            className={`text-sm py-2.5 px-3 rounded-sm border transition-all duration-200 text-left ${
                              active
                                ? "bg-white text-black border-white"
                                : "bg-black/40 text-white/70 border-white/10 hover:border-white/40 hover:text-white"
                            }`}
                          >
                            <span className="inline-flex items-center gap-2">
                              <span
                                className={`h-3.5 w-3.5 inline-flex items-center justify-center rounded-[2px] border ${
                                  active
                                    ? "bg-black border-black"
                                    : "border-white/30"
                                }`}
                              >
                                {active && (
                                  <svg
                                    width="9"
                                    height="9"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                  >
                                    <path
                                      d="M2 6L5 9L10 3"
                                      stroke="white"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </span>
                              {service}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </Field>

                  <Field label="Tell us about your event" required>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      required
                      rows={5}
                      placeholder="Venue, vibe, must-haves, special moments..."
                      data-testid="contact-input-message"
                      className={`${inputBase} resize-none`}
                    />
                  </Field>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                    <p className="text-xs text-white/40 leading-relaxed">
                      We&rsquo;ll only use your details to reply to this
                      inquiry. No spam, ever.
                    </p>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-form-submit"
                      className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#FFFFFF] text-black font-medium hover:bg-[#F5F5F5] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Inquiry
                          <Send className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
